import { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, Search, Pencil, Trash2, MoreHorizontal, Eye, EyeOff, Archive, Package, AlertTriangle, Barcode, ImageIcon, X } from 'lucide-react';
import api from '@/lib/api';
import { formatDateTimeShort } from '@/lib/datetime';
import { cn } from '@/lib/utils';

export default function OwnerStoreProducts() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', description: '', price: '', stock: '', sku: '', status: 'draft' });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [planUsage, setPlanUsage] = useState(null);
    const [planLimitOpen, setPlanLimitOpen] = useState(false);

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const params = { search, page, per_page: 10 };
            if (filter !== 'all') params.status = filter;
            const res = await api.get('/owner/store/products', { params });
            setProducts(res.data.data);
            setMeta({ last_page: res.data.last_page, current_page: res.data.current_page, total: res.data.total });
        } catch {} finally { setLoading(false); }
    }, [search, filter, page]);

    const fetchPlanUsage = useCallback(async () => {
        try {
            const res = await api.get('/owner/store/plan-usage');
            setPlanUsage(res.data.data);
        } catch {}
    }, []);

    useEffect(() => { fetchProducts(); }, [fetchProducts]);
    useEffect(() => { fetchPlanUsage(); }, [fetchPlanUsage]);

    const openCreate = async () => {
        setEditing(null);
        setForm({ name: '', description: '', price: '', stock: '', sku: '', status: 'draft' });
        setImageFile(null);
        setImagePreview(null);
        setError('');
        try {
            const res = await api.get('/owner/store/plan-usage');
            const usage = res.data.data;
            if (usage.limit !== null && usage.current >= usage.limit) {
                setPlanUsage(usage);
                setPlanLimitOpen(true);
                return;
            }
        } catch {}
        setDialogOpen(true);
    };

    const openEdit = (product) => {
        setEditing(product);
        setForm({
            name: product.name,
            description: product.description || '',
            price: String(product.price),
            stock: String(product.stock),
            sku: product.sku || '',
            status: product.status === 'archived' ? 'draft' : product.status,
        });
        setImageFile(null);
        setImagePreview(product.image ? `/storage/${product.image}` : null);
        setError('');
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const payload = new FormData();
            payload.append('name', form.name);
            if (form.description) payload.append('description', form.description);
            payload.append('price', parseFloat(form.price));
            payload.append('stock', parseInt(form.stock));
            if (form.sku) payload.append('sku', form.sku);
            payload.append('status', form.status);
            if (imageFile) {
                payload.append('image', imageFile);
            }
            if (editing) {
                payload.append('_method', 'PUT');
                await api.post(`/owner/store/products/${editing.id}`, payload);
            } else {
                await api.post('/owner/store/products', payload);
            }
            setDialogOpen(false);
            fetchProducts();
            fetchPlanUsage();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar.');
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar este producto?')) return;
        try {
            await api.delete(`/owner/store/products/${id}`);
            fetchProducts();
            fetchPlanUsage();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al eliminar.');
        }
    };

    const handleTogglePublish = async (product) => {
        try {
            await api.patch(`/owner/store/products/${product.id}/publish`);
            fetchProducts();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al cambiar estado.');
        }
    };

    const handleArchive = async (product) => {
        try {
            await api.post(`/owner/store/products/${product.id}/archive`);
            fetchProducts();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al archivar.');
        }
    };

    const stockBadge = (stock) => {
        if (stock <= 0) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-900/50 text-red-300 border border-red-900/50">Sin stock</span>;
        if (stock <= 4) return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-900/50 text-yellow-300 border border-yellow-900/50">Stock bajo</span>;
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/50 text-green-300 border border-green-900/50">En stock</span>;
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Productos</h1>
                <div className="flex items-center gap-2">
                    {planUsage && (
                        <span className={cn("text-xs", planUsage.limit !== null && planUsage.current >= planUsage.limit ? "text-red-400" : "text-white/60")}>
                            {planUsage.current}/{planUsage.limit ?? '∞'} productos
                        </span>
                    )}
                    <Button
                        onClick={openCreate}
                        disabled={planUsage?.limit !== null && planUsage?.current >= planUsage?.limit}
                        className="w-full sm:w-auto"
                        title={planUsage?.limit !== null && planUsage?.current >= planUsage?.limit ? "Alcanzaste el límite de productos de tu plan" : ""}
                    >
                        <Plus className="h-4 w-4 mr-2" />Nuevo Producto
                    </Button>
                </div>
            </div>

            <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                <CardHeader>
                    <div className="flex flex-wrap items-center gap-2">
                        <Search className="h-4 w-4 text-white/60" />
                        <Input placeholder="Buscar productos..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="max-w-sm bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                        <select className="h-9 rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 text-sm text-white" value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}>
                            <option value="all" className="bg-[#0a0a0a] text-white">Todos</option>
                            <option value="published" className="bg-[#0a0a0a] text-white">Publicados</option>
                            <option value="draft" className="bg-[#0a0a0a] text-white">Borradores</option>
                            <option value="archived" className="bg-[#0a0a0a] text-white">Archivados</option>
                        </select>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-white/60 text-center py-8">Cargando...</p>
                    ) : products.length === 0 ? (
                        <p className="text-white/60 text-center py-8">No se encontraron productos.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm min-w-[500px]">
                                    <thead>
                                        <tr className="border-b border-[#3a3a3f]">
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Nombre</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider hidden sm:table-cell">SKU</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Precio</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Stock</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider hidden sm:table-cell">Estado</th>
                                            <th className="text-right py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {products.map(product => (
                                            <tr key={product.id} className={`border-b border-[#3a3a3f] last:border-0 hover:bg-white/5 ${product.status === 'archived' ? 'opacity-60' : ''}`}>
                                                <td className="py-3 px-2 font-medium text-white">
                                                    <div className="flex items-center gap-2">
                                                        {product.image && (
                                                            <img src={`/storage/${product.image}`} alt="" className="h-8 w-8 rounded object-cover shrink-0" />
                                                        )}
                                                        <span className="truncate block max-w-[180px]">{product.name}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2 text-white/60 hidden sm:table-cell">
                                                    <span className="font-mono text-xs">{product.sku || '-'}</span>
                                                </td>
                                                <td className="py-3 px-2 font-semibold text-white">${product.price}</td>
                                                <td className="py-3 px-2">{stockBadge(product.stock)}</td>
                                                <td className="py-3 px-2 hidden sm:table-cell">
                                                    {product.status === 'archived' ? (
                                                        <span className="text-xs text-white/40">Archivado</span>
                                                    ) : product.status === 'published' ? (
                                                        <span className="text-xs text-green-400">Publicado</span>
                                                    ) : (
                                                        <span className="text-xs text-white/40">Borrador</span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-2 text-right">
                                                    <div className="hidden sm:flex justify-end gap-1">
                                                        {product.status !== 'archived' && (
                                                            <>
                                                                <Button variant="ghost" size="icon" onClick={() => handleTogglePublish(product)} title={product.status === 'published' ? 'Despublicar' : 'Publicar'} className="text-white hover:bg-white/5">
                                                                    {product.status === 'published' ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                                </Button>
                                                                <Button variant="ghost" size="icon" onClick={() => handleArchive(product)} title="Archivar" className="text-white hover:bg-white/5">
                                                                    <Archive className="h-4 w-4" />
                                                                </Button>
                                                            </>
                                                        )}
                                                        <Button variant="ghost" size="icon" onClick={() => openEdit(product)} className="text-white hover:bg-white/5">
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" onClick={() => handleDelete(product.id)} className="text-white hover:bg-white/5">
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                    <div className="sm:hidden">
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="icon" className="text-white hover:bg-white/5">
                                                                    <MoreHorizontal className="h-4 w-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                {product.status !== 'archived' && (
                                                                    <>
                                                                        <DropdownMenuItem onClick={() => handleTogglePublish(product)} className="gap-2">
                                                                            {product.status === 'published' ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                                            {product.status === 'published' ? 'Despublicar' : 'Publicar'}
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem onClick={() => handleArchive(product)} className="gap-2">
                                                                            <Archive className="h-4 w-4" /> Archivar
                                                                        </DropdownMenuItem>
                                                                    </>
                                                                )}
                                                                <DropdownMenuItem onClick={() => openEdit(product)} className="gap-2">
                                                                    <Pencil className="h-4 w-4" /> Editar
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem onClick={() => handleDelete(product.id)} className="gap-2 text-red-400 focus:text-red-400">
                                                                    <Trash2 className="h-4 w-4" /> Eliminar
                                                                </DropdownMenuItem>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {meta.last_page > 1 && (
                                <div className="flex justify-center gap-2 mt-4">
                                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-[#3a3a3f] text-white hover:bg-white/5">Anterior</Button>
                                    <span className="flex items-center text-sm text-white/60">Página {meta.current_page} de {meta.last_page}</span>
                                    <Button variant="outline" size="sm" disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)} className="border-[#3a3a3f] text-white hover:bg-white/5">Siguiente</Button>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white">
                    <DialogHeader>
                        <DialogTitle className="text-white uppercase tracking-wide">{editing ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                        {error && <div className="bg-white/5 text-white text-sm p-3 rounded-md">{error}</div>}
                        <div className="space-y-2">
                            <Label className="text-white/60 uppercase tracking-wider text-xs">Nombre</Label>
                            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white/60 uppercase tracking-wider text-xs">Descripción (opcional)</Label>
                            <textarea className="flex min-h-[60px] w-full rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 py-2 text-sm text-white placeholder:text-white/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Precio ($)</Label>
                                <Input type="number" min="0" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Stock</Label>
                                <Input type="number" min="0" step="1" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} required className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">SKU (opcional)</Label>
                                <Input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} placeholder="Ej: PROD-001" className="bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Imagen (opcional)</Label>
                                <div className="flex items-center gap-3">
                                    <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="border-[#3a3a3f] text-white hover:bg-white/5">
                                        <ImageIcon className="h-4 w-4 mr-2" />
                                        {imagePreview ? 'Cambiar' : 'Subir'}
                                    </Button>
                                    {(imagePreview || editing?.image) && (
                                        <Button type="button" variant="ghost" size="icon" onClick={() => { setImageFile(null); setImagePreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-white/60 hover:text-white hover:bg-white/5">
                                            <X className="h-4 w-4" />
                                        </Button>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                setImageFile(file);
                                                setImagePreview(URL.createObjectURL(file));
                                            }
                                        }}
                                    />
                                </div>
                                {imagePreview && (
                                    <img src={imagePreview} alt="Preview" className="mt-2 h-20 w-20 rounded object-cover border border-[#3a3a3f]" />
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch checked={form.status === 'published'} onCheckedChange={v => setForm({ ...form, status: v ? 'published' : 'draft' })} id="status" />
                            <Label htmlFor="status" className="text-white/60 uppercase tracking-wider text-xs cursor-pointer">Publicado</Label>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5">Cancelar</Button>
                            <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={planLimitOpen} onOpenChange={setPlanLimitOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-white uppercase tracking-wide">
                            <AlertTriangle className="h-5 w-5 text-yellow-400" />
                            Límite de productos alcanzado
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-white/80">
                        Tu plan actual permite un máximo de {planUsage?.limit} producto{planUsage?.limit !== 1 ? 's' : ''}.
                        Actualmente tenés {planUsage?.current} producto{planUsage?.current !== 1 ? 's' : ''}.
                    </p>
                    <p className="text-sm text-white/60">
                        Archivá productos que ya no uses o actualizá tu plan para agregar más.
                    </p>
                    <DialogFooter>
                        <Button onClick={() => setPlanLimitOpen(false)} className="w-full sm:w-auto">Entendido</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
